import React, { useEffect, useRef, memo } from 'react';
import "./index.css"

export interface MegaScrollPageProps {
    children: React.ReactNode;
}

export const MegaScrollPage = ({ children }: MegaScrollPageProps) => {
    const ref = useRef<HTMLDivElement>(null);

    return (
        <div ref={ref} className='ms-item'>
            {children}
        </div>
    )
}

export interface MegaScrollProps {
    children?: React.ReactNode;
    onChange?: (index: number) => void;
    noFocus?: boolean;
}

const MegaScroll = ({ children, onChange, noFocus }: MegaScrollProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const childs = React.Children.toArray(children);
    const rafIdRef = useRef<number>(0);
    const lastIndexRef = React.useRef<number>(-1);
    const settleTimerRef = React.useRef<number>(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const getSlideHeight = () => container.clientHeight;

        const findSlideIndex = () => {
            const slideHeight = getSlideHeight();
            if (slideHeight === 0) return -1;
            const scrollTop = container.scrollTop;
            const index = Math.round(scrollTop / slideHeight);
            return Math.max(0, Math.min(index, childs.length - 1));
        };

        const notify = (idx: number) => {
            if (idx < 0 || !onChange) return;

            const targetIndex = container.dataset.targetIndex;
            if (targetIndex !== undefined) {
                // Programmatic scroll in progress from NavigationContext
                // Only report once we've reached the intended target
                if (idx !== parseInt(targetIndex, 10)) {
                    lastIndexRef.current = idx;
                    return;
                }
                container.removeAttribute('data-target-index');
            }

            if (idx !== lastIndexRef.current) {
                lastIndexRef.current = idx;
                onChange(idx);
            }
        };

        const onScroll = () => {
            if (rafIdRef.current) return;
            rafIdRef.current = requestAnimationFrame(() => {
                const idx = findSlideIndex();
                notify(idx);
                rafIdRef.current = 0;

                // Re-check after CSS scroll-snap settles & correct sub-pixel drift
                clearTimeout(settleTimerRef.current);
                settleTimerRef.current = window.setTimeout(() => {
                    const settledIdx = findSlideIndex();
                    notify(settledIdx);

                    // Force-align to exact slide boundary to fix Mac sub-pixel peek
                    const slideHeight = getSlideHeight();
                    const expectedTop = settledIdx * slideHeight;
                    const drift = Math.abs(container.scrollTop - expectedTop);
                    if (drift > 0.5 && drift < slideHeight * 0.1) {
                        container.scrollTo({ top: expectedTop, behavior: 'instant' as ScrollBehavior });
                    }
                }, 400);
            });
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        const initTimer = window.setTimeout(() => notify(findSlideIndex()), 200);

        return () => {
            container.removeEventListener('scroll', onScroll);
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
            clearTimeout(initTimer);
            clearTimeout(settleTimerRef.current);
        };
    }, [childs.length, onChange]);

    useEffect(() => {
        if (!noFocus) {
            containerRef.current?.focus();
        }
    }, [noFocus]);

    return (
        <div ref={containerRef} className="ms-container" tabIndex={0}>
            {childs.map((child, i) => (
                <MegaScrollPage key={i}>
                    {child}
                </MegaScrollPage>
            ))}
        </div>
    )
}

export default memo(MegaScroll);
